import { displayLinesToSeparateLines } from '@/utils/math-live'
import {
  LineChartNumberDataset,
  firebaseGetSingleSubCollectionDocFromDb,
} from '@/firebase/utils'
import {
  ContentFileUploadType,
  TContentFileUpload,
  IGenericError,
  IGptResponseArguments,
  IHistoryItem,
  IHistoryItemMultipleSelect,
  IHistoryItemOpenTextFulfilled,
  IHistoryItemRaw,
  IHistoryMessageContentObjectTutorFeedback,
  ILesson,
  ILessonContent,
  ILessonContentOpenText,
  ILessonVariantMatchingLines,
  ILessonVariantMultipleChoice,
  ILessonVariantMultipleSelect,
  IMatchingLinesConnection,
  IMatchingLinesValue,
  IMultipleSelectValue,
  IPracticeQuestion,
  MessageRole,
  OpenTextLessonConversationFlowState,
  StudentAnswerStatus,
  THistoryMessage,
  THistoryMessageMultipleSelect,
  THistoryMessageOpenText,
  TLastVariantOpenText,
  TLessonVariant,
  TMessageContentObject,
  IContentFileUploadImage,
  THistoryMessageDoc,
  CourseSubject,
  LessonChatMode,
  TMultiModeChatHistoryMessageOpenText,
  ILessonContentMultiModeChatOpenText,
  IMultiModeChatHistoryItemOpenTextFulfilled,
  LessonChatSubMode,
  IDynamicPaperTopicLesson,
  IPostExamMessageInternal,
  PostExamMessageRole,
  IGptConversationMessage,
} from '../types'
import { getLessonVariantsById } from '@/firebase/lessons-ts'
import shuffleArray from '@/utils/shuffleArray'
import {
  DEFAULT_MMR,
  K_FACTOR,
  calculateExpectedOutcome,
  getMaxLossForGame,
} from './constants'
import { getPracticeQuestions } from '@/firebase/practice-questions'
import { notify } from '.'
import { isArray } from 'lodash'
import { safeJsonParse } from './safeJsonParse'
import { $getRoot, createEditor } from 'lexical'
import { getHistoryConversationMessages } from '../firebase'
import { IQuestionChoiceData } from '@/types/openai'

export function findClosestVariant(
  studentMmr: number,
  variants: TLessonVariant[],
): TLessonVariant {
  let closestVariant = variants[0]
  let smallestDifference = Math.abs(studentMmr - variants[0].MMR)

  for (let i = 1; i < variants.length; i++) {
    let currentDifference = Math.abs(studentMmr - variants[i].MMR)
    if (
      currentDifference < smallestDifference ||
      (currentDifference === smallestDifference &&
        variants[i].MMR < closestVariant.MMR)
    ) {
      closestVariant = variants[i]
      smallestDifference = currentDifference
    }
  }

  return closestVariant
}

export function findNextVariant(
  studentMmr: number,
  variants: TLessonVariant[],
  usedVariantsIds: string[] = [],
): TLessonVariant | undefined {
  let filteredVariants: TLessonVariant[] = [...variants]
  // sort variants by MMR value in ascending order [1500, 1200, 1800 ==> 1200, 1500, 1800]
  // used to provide variants in ascending difficulty progression
  filteredVariants = filteredVariants.sort((a, b) => a.MMR - b.MMR)

  // remove already answered variants
  filteredVariants = filteredVariants.filter(
    (v) => !usedVariantsIds.includes(v.id),
  )

  if (!filteredVariants.length) {
    return undefined
  }

  // remove variants with lesser MMR value than student already has
  filteredVariants = filteredVariants.filter((v) => v.MMR >= studentMmr)

  if (!filteredVariants.length) {
    return undefined
  }

  // return first variant from variants array
  return filteredVariants[0]
}

export const getLessonContentById = async (
  lesson: ILesson,
  bookedCourseId: string,
  studentId: string,
): Promise<ILessonContent> => {
  try {
    const documentId = `${bookedCourseId}-${lesson.id}`

    const [histories, variants, messages] = await Promise.all([
      firebaseGetSingleSubCollectionDocFromDb<IHistoryItemRaw, IHistoryItem>(
        'histories',
        studentId,
        'items',
        documentId,
      ),
      getLessonVariantsById(lesson.id),
      getHistoryConversationMessages(studentId, documentId),
    ])

    const isAnswered =
      !!histories?.isLessonCompleted || !!histories?.isCompleted

    const messagesOrLegacyMessages = (
      !!messages.length
        ? messages
        : !!histories?.messages?.length
        ? histories.messages
        : []
    ) as THistoryMessageDoc[] // type annotation is used to handle legacy messages saved as array, these has no id field instead of THistoryMessageDoc

    const lessonContent: ILessonContent = {
      ...lesson,
      variants,
      histories,
      messages: messagesOrLegacyMessages,
      isAnswered,
    }

    return lessonContent
  } catch (error: unknown) {
    console.error(
      'error getLessonContentById :>> ',
      (error as IGenericError)?.message || error,
    )

    throw error
  }
}

export function shuffleVariantsValues(
  variant: ILessonVariantMultipleChoice | ILessonVariantMultipleSelect,
) {
  const shuffledValues = shuffleArray(variant.values)

  const updatedVariant = {
    ...variant,
    values: shuffledValues,
  }

  return updatedVariant
}

export function removeBase64Images(string?: string): string | undefined {
  // Safety check to ensure we're working with a string
  if (typeof string !== 'string') {
    return string
  }

  try {
    // This regular expression finds 'data:image' MIME types (e.g., data:image/png;base64,...)
    var regex = /data:image\/([a-zA-Z]*);base64,([^\"]*)/g
    return string.replace(regex, '')
  } catch (error) {
    // If an error occurs during the replace operation, return the original string
    console.error('Error in removeBase64Images:', error)
    return string
  }
}

const solutionInitialPrompt = `
			1. Text Formatting:
				- Always use double backslashes for LaTeX but not for quotes: \\\\text, \\\\sqrt, \\\\cdot

			2. Text Structure:
				- Start each step with "Step N:" followed by new line
				- Add a blank new line between steps
				- End with "Final Answer:" followed by a new line

			3. Step Format:
				Step N: <step description>
				<question working>
				<explanation>

				Step Example Format:
					Step 1: Identify the question
					The question asks: "What does the text explicitly state about the weather in the market-square?"
					We need to extract the explicit mention from the text.

					Step 2: Extract the explicit detail
					The text explicitly states that the morning sun shone brightly upon the market-square.
					This sentence is a direct quotation of the detail provided in the text.

					Final Answer:
					The text explicitly states that the morning sun shone brightly upon the market-square.
`

const solutionMathPrompt = `
				1. Text Formatting:
					- Always use **DOUBLE** backslashes for all LaTeX commands: \\\\text, \\\\sqrt, \\\\cdot, \\\\begin, \\\\end, \\\\hline.
					- Do NOT escape normal quotes.

				2. Text Structure:
					- Start each step with "Step N:" followed by a new line
					- Add a blank line between steps
					- Add a blank line before and after block equations
					- End with "Final Answer:" followed by a new line

				3. Step Format:
						Step N: <Description>
						<Mathematical working (on a single line within $$...$$)>
						<Explanation>

					Step Example Format:
						Step 1: Identify the equation
						$$ax^2 + bx + c = 0$$
						This is our quadratic equation.

						Step 2: Apply the quadratic formula
						$$x = \\\\frac{-b \\\\pm \\\\sqrt{b^2 - 4ac}}{2a}$$
						Using this formula gives us our solution

						Final Answer:
						$x = 2$ or $x = -3$
`

const mathPromptSubjects: CourseSubject[] = [
  CourseSubject.Maths,
  CourseSubject.Physics,
  CourseSubject.Chemistry,
]

export function getSolutionPromptPart(subject?: CourseSubject): string {
  let solutionPromptPart: string = solutionInitialPrompt

  if (subject && mathPromptSubjects.includes(subject)) {
    solutionPromptPart = solutionMathPrompt
  }

  return solutionPromptPart
}

export interface IPromptState {
  question?: string
  questionTextInput?: string
  hintsAndAnswersFromDB?: string[]
  correctAnswer?: string
  questionFromDB?: string
  hints?: string[]
  usedVariants?: string[]
  solutionPromptPart?: string
  choiceData?: IQuestionChoiceData
}

export function generatePrompt(promptState: IPromptState): string {
  // question.question used to be the sole question text that was used for display and prompt
  // now we have `questionTextInput` which may ormaynot exists (legacy questions will not have it)
  // So we need to check if it exists and use it if it does
  const questionForDisplay = removeBase64Images(promptState.question)
  const questionTextInput = promptState.questionTextInput || questionForDisplay

  const correctSolution = promptState.correctAnswer

  // Splitting into parts to maintain readability while eliminating newline (\n) characters in request payload
  const promptParts = [
    `As an AI tutor, your task is to provide supportive feedback on student answers while considering their learning journey and previous interactions. Return responses in a JSON object containing the 'gptResponseToStudent' field.

    Core Technical Requirements:
    1. Question Context:
    - Displayed question: ${questionForDisplay || ''}\n
    - Question to interpret: ${questionTextInput || ''}\n
    - Correct solution: ${correctSolution || ''}\n
    - Available hints: ${JSON.stringify(
      promptState.hints || promptState.hintsAndAnswersFromDB || [],
    )}\n
		- Request for solution pattern: "${solutionRequestText || ''}"

    2. Mathematical Expression Formatting:
			- Inline math: Use $...$
				Example: "The value of $x^2$ is..."
			- Block math: Use $$...$$ on **ONE LOGICAL LINE ONLY**.
				Example (CORRECT): "The value of $$x^2$$ is..."
			- Block equations MUST NOT contain real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
			- **RULE FOR MULTI-LINE STRUCTURES (e.g., arrays):** Complex or multi-line LaTeX environments (e.g., \\\\begin{array})**MUST BE** entirely enclosed within $$...$$ and remain on a single logical line, with no real line breaks (\\n) or surrounding 		whitespace inside the $$...$$ delimiters.
				Example (CORRECT): "$$ \\\\begin{array}{r} 37.80\\\\ + 8.97\\\\ \\\\hline \\\\end{array} $$"
			- **All** LaTeX commands must use **DOUBLE** backslashes.
				Example (CORRECT): "$u = 10\\\\frac{m}{s}$"
			- Do NOT use \\( \\), \\[ \\], or any other math delimiters.
			- Use ONLY $...$ for inline math and $$...$$ for block math.
			- Always use double backslashes ("\\\\") instead of single backslashes: \\\\frac, \\\\sqrt, \\\\cdot .
				Example: "$u = 10\\\\frac{m}{s}$" (correct).
			- No single backslashes, no unescaped LaTeX.
			- Avoid: "$u = 10\\frac{m}{s}$" or "$u = 10\ \frac{m}{s}$" (incorrect)
			- Review all KaTeX expressions for proper formatting.
			- If studentAnswerIncludesKaTexSyntax is "true", verify KaTeX syntax in your response.

			3. Currency Notation Rule:
			- For all monetary amounts, prices, or financial calculations, ALWAYS use the text-based currency code (USD, EUR, GBP) or the full currency name (e.g., "dollars") instead of their symbols.
			
    Response Approach:

    1. Initial Assessment:
    - Consider the student's previous attempts and current understanding
    - Look for partial comprehension and effort
    - Start with positive acknowledgment of any correct elements

    2. Feedback Categories:

		A. Success Case (Correct Answer):
		When the student's response meets the requirements defined in the Correct solution (Mark Scheme), even if the presentation differs:
		{
			"gptResponseToStudent": "Excellent! [Briefly validate how their answer meets the mark scheme]. You've demonstrated a clear and accurate understanding of [concept]. Well done!",
		}
			Guidelines for Success:
				- Mark Scheme Alignment: The primary benchmark for correctness is the content of the Correct solution. If the student provides the necessary information to "earn the mark" according to the marking criteria, treat it as correct.
				- Substance Over Syntax: Accept answers that are logically identical to the Mark Scheme, even if they lack LaTeX formatting, use different punctuation, or vary in capitalization.
				- Conceptual Equivalence: Accept "own-word" explanations or paraphrasing, provided they accurately convey the scientific/mathematical technicalities required by the Mark Scheme.
				- Zero-Tolerance for False Corrections: Do not provide "constructive feedback" or suggest errors (such as claiming items are swapped or missing) if the student's answer already satisfies the Mark Scheme.
				- Direct Validation: Once the student hits the required points, transition immediately from prompting to confirming. Avoid asking further leading questions that might confuse a student who has already succeeded.

    B. Making Progress (Attempted but Incorrect):
    When the answer shows understanding but isn't fully correct:
    {
      "gptResponseToStudent": "I see you're using [specific correct element]. That shows good understanding of [concept]. Let's explore [area for improvement] together. You're getting closer to the solution!"
    }

    C. Need Direction (Unrelated Answer):
    When the answer shows confusion or misunderstanding:
    {
      "gptResponseToStudent": "Thank you for trying this problem! Let's look at it from a different angle. The question is asking us to [rephrase question without quoting]. I'm here to help you work through this."
    }

    Guidelines:
		1. Response Prioritization:
      - If the student's answer matches the Mark Scheme (Success Case), confirm it immediately.
      - Never reveal the full step-by-step solution until:
        - Last student message is: "${solutionRequestText || ''}"
        - the student demonstrates clear understanding
        - the student has already achieved the correct answer.

    2. Question Clarification:
      - If student asks about the question, rephrase it differently
      - Don't repeat questionForDisplay or questionTextInput verbatim
      - Focus on conceptual understanding

    3. Hint Progression (escalating - every hint must go further than the last):
      - Use hints array sequentially when hints are available
      - Before writing a hint, scan the conversation for hints you have ALREADY given on this question. The new hint MUST be more specific and actionable than every previous one - never restate or lightly rephrase an earlier hint.
      - Escalation ladder: 1st hint = point at the approach (which concept applies, what to look at). 2nd hint = name the concrete first step, formula, or substitution to use. 3rd and later hints = walk through the method step by step, stopping just short of the final answer.
      - Build on student's current understanding
      - Connect hints to student's previous attempts

    4. Error Handling:
      - Treat ambiguous answers as opportunities for clarification
      - Ask about student's approach when unclear
      - Default to "Making Progress" category if uncertain

    Technical Processing:
    1. JSON Structure:
      - Maintain nested structure as needed
      - Include all required metadata
      - Format mathematical expressions correctly

    2. Expression Validation:
      - Use kaTexOperatorsArray to identify mathematical expressions
      - Verify proper KaTeX syntax
      - Flag any potential syntax errors

    Sample Progressive Response Flow:
    1. First Attempt:
      - Acknowledge effort
      - Identify specific elements showing understanding
      - Provide first hint

    2. Follow-up Attempts:
      - Reference previous work
      - Build on established understanding
      - Offer next sequential hint

    3. Final Guidance:
      - Celebrate progress
      - Connect concepts
      - Confirm understanding

		Solution Response Approach:
			- If last students message is "${
        solutionRequestText || ''
      }", then **focus solely on the step-by-step solution**:
      - Do **not** provide general feedback or hints.
      - Only include the structured solution breakdown.
			- If last students message is not "${
        solutionRequestText || ''
      }", then **provide encouraging feedback** on the student's answer and offer hints as needed.
			- Keep in mind that a request for a solution usually means the student doesn't know how to answer the question.
		`,
  ]

  // Append solution prompt if provided
  if (promptState.solutionPromptPart) {
    promptParts.push(
      `
		Solution Response Guidelines: 
			**Solution Response Structure:**
				When providing a solution (only when last student message is "${
          solutionRequestText || ''
        }"), 
				include these fields in the JSON response:
					{
						"gptResponseToStudent": "[Step-by-step explanation including the final answer]",
						"commonMistake": "[Description of common errors students make with this problem type]",
						"tip": "[Helpful strategy or shortcut for similar problems]",
						"isSolutionResponse": true
					}

			**Field Usage Guidelines for Solution Response:**
				1.  **gptResponseToStudent** - REQUIRED field for all solution responses:
				    -   Must contain the **complete, step-by-step breakdown of how to solve the problem and show the final answer.**
						-   The explanation should be clear, detailed, and follow all specified formatting rules.

				2.  **commonMistake** - Include when:
						-   There's a typical misconception worth highlighting.
						-   The error pattern is common for this type of problem.
						-   It helps prevent future mistakes.
						-   Format: "Students often [describe error] because [reason]. Remember that [correction]."

				3.  **tip** - Include when:
						-   There's a reusable problem-solving strategy.
						-   A particular approach simplifies the solution.
						-   It helps generalize the learning.
						-   Format: "For similar problems, try [strategy] because [reason]."

			**Solution Response Structure and Formatting rules:**
				${promptState.solutionPromptPart}
    `,
    )
  }

  // add general reminders
  promptParts.push(`
		Remember:
			- Maintain encouraging tone throughout
			- Guide rather than tell
			- Focus on learning process
			- Keep technical requirements intact
			- Build on previous interactions
		`)

  const prompt = promptParts.join(' ')

  return prompt
}

export interface IDpPromptState {
  question?: string
  correctAnswer?: string
  solutionPromptPart?: string
  choiceData?: IQuestionChoiceData
}

export function generateDpPrompt(promptState: IDpPromptState): string {
  let questionForDisplay = removeBase64Images(promptState.question)
  let correctSolution = promptState.correctAnswer

  if (promptState.choiceData) {
    questionForDisplay = promptState.choiceData.question
    correctSolution = ''
    let optionsText = promptState.choiceData.options.reduce(
      (acc: string, next) => {
        const optionContent = next.content
        const optionLabel = next.id

        // set correct solution
        if (next.isCorrect) {
          correctSolution = `${correctSolution}\n - ${optionLabel}. ${optionContent}\n`
        }

        // update acc
        return `${acc}\n - ${optionLabel}. ${optionContent}`
      },
      '',
    )

    // add choiceData options to question for display
    questionForDisplay = `${questionForDisplay}${optionsText}\n`
  }

  // Splitting into parts to maintain readability while eliminating newline (\n) characters in request payload
  const promptParts = [
    `As an AI tutor, your task is to provide supportive feedback on student answers while considering their learning journey and previous interactions. Return responses in a JSON object containing the 'gptResponseToStudent' field.

    Core Technical Requirements:
    1. Question Context:
			• Question: ${questionForDisplay || ''}
			• Correct solution: ${correctSolution || ''}
			• Request for solution pattern: "${solutionRequestText || ''}"

    2. Mathematical Expression Formatting:
			- Inline math: Use $...$
				Example: "The value of $x^2$ is..."
			- Block math: Use $$...$$ on **ONE LOGICAL LINE ONLY**.
				Example (CORRECT): "The value of $$x^2$$ is..."
			- Block equations MUST NOT contain real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
			- **RULE FOR MULTI-LINE STRUCTURES (e.g., arrays):** Complex or multi-line LaTeX environments (e.g., \\\\begin{array})**MUST BE** entirely enclosed within $$...$$ and remain on a single logical line, with no real line breaks (\\n) or surrounding 		whitespace inside the $$...$$ delimiters.
				Example (CORRECT): "$$ \\\\begin{array}{r} 37.80\\\\ + 8.97\\\\ \\\\hline \\\\end{array} $$"
			- **All** LaTeX commands must use **DOUBLE** backslashes.
				Example (CORRECT): "$u = 10\\\\frac{m}{s}$"
			- Do NOT use \\( \\), \\[ \\], or any other math delimiters.
			- Use ONLY $...$ for inline math and $$...$$ for block math.
			- Always use double backslashes ("\\\\") instead of single backslashes: \\\\frac, \\\\sqrt, \\\\cdot .
				Example: "$u = 10\\\\frac{m}{s}$" (correct).
			- No single backslashes, no unescaped LaTeX.
			- Avoid: "$u = 10\\frac{m}{s}$" or "$u = 10\ \frac{m}{s}$" (incorrect)
			- Review all KaTeX expressions for proper formatting.
			- If studentAnswerIncludesKaTexSyntax is "true", verify KaTeX syntax in your response.

			3. Currency Notation Rule:
			- For all monetary amounts, prices, or financial calculations, ALWAYS use the text-based currency code (USD, EUR, GBP) or the full currency name (e.g., "dollars") instead of their symbols.
			
    Response Approach:
    1. Initial Assessment:
    - Consider the student's previous attempts and current understanding
    - Look for partial comprehension and effort
    - Start with positive acknowledgment of any correct elements

    2. Feedback Categories:
		A. Success Case (Correct Answer):
		When the student's response meets the requirements defined in the Correct solution (Mark Scheme), even if the presentation differs:
		{
			"gptResponseToStudent": "Excellent! [Briefly validate how their answer meets the mark scheme]. You've demonstrated a clear and accurate understanding of [concept]. Well done!",
		}
			Guidelines for Success:
				- Mark Scheme Alignment: The primary benchmark for correctness is the content of the Correct solution. If the student provides the necessary information to "earn the mark" according to the marking criteria, treat it as correct.
				- Substance Over Syntax: Accept answers that are logically identical to the Mark Scheme, even if they lack LaTeX formatting, use different punctuation, or vary in capitalization.
				- Conceptual Equivalence: Accept "own-word" explanations or paraphrasing, provided they accurately convey the scientific/mathematical technicalities required by the Mark Scheme.
				- Zero-Tolerance for False Corrections: Do not provide "constructive feedback" or suggest errors (such as claiming items are swapped or missing) if the student's answer already satisfies the Mark Scheme.
				- Direct Validation: Once the student hits the required points, transition immediately from prompting to confirming. Avoid asking further leading questions that might confuse a student who has already succeeded.

    B. Making Progress (Attempted but Incorrect):
    When the answer shows understanding but isn't fully correct:
    {
      "gptResponseToStudent": "I see you're using [specific correct element]. That shows good understanding of [concept]. Let's explore [area for improvement] together. You're getting closer to the solution!"
    }

    C. Need Direction (Unrelated Answer):
    When the answer shows confusion or misunderstanding:
    {
      "gptResponseToStudent": "Thank you for trying this problem! Let's look at it from a different angle. The question is asking us to [rephrase question without quoting]. I'm here to help you work through this."
    }

    Guidelines:
		1. Response Prioritization:
      - If the student's answer matches the Mark Scheme (Success Case), confirm it immediately.
      - Never reveal the full step-by-step solution until:
        - Last student message is: "${solutionRequestText || ''}"
        - the student demonstrates clear understanding
        - the student has already achieved the correct answer.

    2. Question Clarification:
      - If student asks about the question, rephrase it differently
      - Don't repeat questionForDisplay or questionTextInput verbatim
      - Focus on conceptual understanding

    3. Hint Progression (escalating - every hint must go further than the last):
      - Use hints array sequentially when hints are available
      - Before writing a hint, scan the conversation for hints you have ALREADY given on this question. The new hint MUST be more specific and actionable than every previous one - never restate or lightly rephrase an earlier hint.
      - Escalation ladder: 1st hint = point at the approach (which concept applies, what to look at). 2nd hint = name the concrete first step, formula, or substitution to use. 3rd and later hints = walk through the method step by step, stopping just short of the final answer.
      - Build on student's current understanding
      - Connect hints to student's previous attempts

    4. Error Handling:
      - Treat ambiguous answers as opportunities for clarification
      - Ask about student's approach when unclear
      - Default to "Making Progress" category if uncertain

    Technical Processing:
    1. JSON Structure:
      - Maintain nested structure as needed
      - Include all required metadata
      - Format mathematical expressions correctly

    2. Expression Validation:
      - Use kaTexOperatorsArray to identify mathematical expressions
      - Verify proper KaTeX syntax
      - Flag any potential syntax errors

    Sample Progressive Response Flow:
    1. First Attempt:
      - Acknowledge effort
      - Identify specific elements showing understanding
      - Provide first hint

    2. Follow-up Attempts:
      - Reference previous work
      - Build on established understanding
      - Offer next sequential hint

    3. Final Guidance:
      - Celebrate progress
      - Connect concepts
      - Confirm understanding

		Solution Response Approach:
			- If last students message is "${
        solutionRequestText || ''
      }", then **focus solely on the step-by-step solution**:
      - Do **not** provide general feedback or hints.
      - Only include the structured solution breakdown.
			- If last students message is not "${
        solutionRequestText || ''
      }", then **provide encouraging feedback** on the student's answer and offer hints as needed.
			- Keep in mind that a request for a solution usually means the student doesn't know how to answer the question.
		`,
  ]

  // Append solution prompt if provided
  if (promptState.solutionPromptPart) {
    promptParts.push(
      `
		Solution Response Guidelines: 
			**Solution Response Structure:**
				When providing a solution (only when last student message is "${
          solutionRequestText || ''
        }"), 
				include these fields in the JSON response:
					{
						"gptResponseToStudent": "[Step-by-step explanation including the final answer]",
						"commonMistake": "[Description of common errors students make with this problem type]",
						"tip": "[Helpful strategy or shortcut for similar problems]",
						"isSolutionResponse": true
					}

			**Field Usage Guidelines for Solution Response:**
				1.  **gptResponseToStudent** - REQUIRED field for all solution responses:
				    -   Must contain the **complete, step-by-step breakdown of how to solve the problem and show the final answer.**
						-   The explanation should be clear, detailed, and follow all specified formatting rules.

				2.  **commonMistake** - Include when:
						-   There's a typical misconception worth highlighting.
						-   The error pattern is common for this type of problem.
						-   It helps prevent future mistakes.
						-   Format: "Students often [describe error] because [reason]. Remember that [correction]."

				3.  **tip** - Include when:
						-   There's a reusable problem-solving strategy.
						-   A particular approach simplifies the solution.
						-   It helps generalize the learning.
						-   Format: "For similar problems, try [strategy] because [reason]."

			**Solution Response Structure and Formatting rules:**
				${promptState.solutionPromptPart}
    `,
    )
  }

  // add general reminders
  promptParts.push(`
		Remember:
			- Maintain encouraging tone throughout
			- Guide rather than tell
			- Focus on learning process
			- Keep technical requirements intact
			- Build on previous interactions
		`)

  const prompt = promptParts.join(' ')

  return prompt
}

export interface IExamSimulationPromptState {
  questionTitle: string
  question?: string
  correctAnswer?: string
  totalMarks?: number
  choiceData?: IQuestionChoiceData
}

export function generateExamSimulationPrompt(
  promptState: IExamSimulationPromptState,
): string {
  let questionForDisplay = promptState.question || ''
  let correctAnswer = promptState.correctAnswer || 'N/A'

  if (promptState.choiceData) {
    questionForDisplay = promptState.choiceData.question
    correctAnswer = ''
    let optionsText = promptState.choiceData.options.reduce(
      (acc: string, next) => {
        const optionContent = next.content
        const optionLabel = next.id

        // set correct solution
        if (next.isCorrect) {
          correctAnswer = `${correctAnswer}\n - ${optionLabel}. ${optionContent}`
        }

        // update acc
        return `${acc}\n - ${optionLabel}. ${optionContent}`
      },
      '',
    )

    // add choiceData options to question for display
    questionForDisplay = `${questionForDisplay}\n${optionsText}`
  }

  // Splitting into parts to maintain readability while eliminating newline (\n) characters in request payload
  const promptParts = [
    `You are an **AI exam evaluator and friendly tutor** participating in a *real exam simulation mode*.`,

    `### Your role
		- You act as a professional examiner who also maintains a friendly and motivating tone.
		- You **do not** provide hints, detailed explanations, or tutoring.
		- You **evaluate** the student's final answer to a single question and provide short, constructive feedback.`,

    `### Context
		- This message belongs to one question from a larger simulated exam session.
		- The student will send **only one message** as their final answer.
		- There will be **no follow-up** or clarification — treat this answer as final.`,

    `### Question info
		- **Question Title**: ${promptState.questionTitle}\n
		- **Displayed question text**: ${questionForDisplay}\n
		- **Correct solution (for evaluation only, do not reveal)**: ${correctAnswer}\n
		- **Total marks available**: ${promptState.totalMarks}`,

    `### Your task
		  When the student sends their answer, analyze it and return a concise evaluation message in **JSON format**.
			This message will be stored as part of the exam transcript and later used to generate the exam overall feedback.`,

    `### Response format (strict)
			Respond **only** with a JSON object like this:
			{
				"examEvaluation": {
					"title": "${promptState.questionTitle}",
					"questionSummary": "[Rewrite the question briefly in your own words, 1 short sentence]",
					"gptResponseToStudent": "[Friendly and professional message (3-5 sentences total). Start with a short acknowledgment of what the student attempted, then provide balanced feedback. Encourage effort, point out strengths, and mention one area to improve. Keep it warm and natural, without revealing the correct answer.]",
					"totalMarks": ${promptState.totalMarks},
					"actualMarks": "[number of marks the student has earned based on their response]"
					}
			}`,

    `### Response style
		- Short, clear, and exam-like — but with warmth and personality.
		- Keep tone supportive, slightly conversational, and encouraging.
		- Mention strong points even if the answer is incorrect.
		- Use natural, human phrasing (avoid overly formal or robotic tone).
		- Do not provide hints or reveal the solution.
		- Keep the total output **under 150 words**.`,

    `### Examples of tone
		“Good effort! You've shown understanding of how brackets expand, even though a small mistake affected the final expression.”  
		“Nice try — you remembered the key idea but missed one term. Review how negative signs distribute.”  
		“You're on the right track! A bit more attention to detail next time and you'll nail it.”`,
  ]

  const prompt = promptParts.join('\n\n')

  return prompt
}

export function generateExamGeneralFeedbackSystemMessage({
  examTitle,
  resultTime,
}: {
  examTitle: string
  resultTime?: string
}): string {
  const promptParts: string[] = []

  // Role intro
  promptParts.push(
    `=== Intro & Role ===
		You are an **AI exam tutor** acting as a professional examiner and friendly mentor.`,
  )

  // Tone
  promptParts.push(
    `=== Tone & Task Description ===
		Your task is to provide a **comprehensive post-exam analysis** for the exam "${examTitle}". 
    Maintain a polite, friendly, encouraging, and professional tone. 
    Be supportive, motivating, and constructive in your explanations.`,
  )

  // Instructions on what to do in general feedback
  promptParts.push(
    `=== AI Instructions ===
		Your responsibilities include:
      1. Summarize the student's overall performance and progress.
      2. Highlight strengths and weaknesses.
      3. ${
        resultTime
          ? `Reflect on the student's time management during the exam.`
          : `Explicitly state that the exam was taken without a timer and that time management was not evaluated.`
      }
      4. Explain how the exam stop reason might have influenced the outcome.
      5. Mention strong and weak topics.
      6. Acknowledge the total marks achieved, unanswered questions, and progress.
			7. Generate a set of short **quick response prompts** that anticipate student follow-up questions or actions, based on the feedback.
      8. Never invent information beyond what is provided by the user message.`,
  )

  // Response format & rules
  promptParts.push(
    `=== Response format & Rules ===
		Your responsibilities include:
      1. Summarize the student's overall performance and progress.
      2. Highlight strengths and weaknesses.
      3. Reflect on the student's time management during the exam.
      4. Explain how the exam stop reason might have influenced the outcome.
      5. Mention strong and weak topics.
      6. Acknowledge the total marks achieved, unanswered questions, and progress.
			7. Generate a set of short **quick response prompts** that anticipate student follow-up questions or actions, based on the feedback.
			8. Use line break (\\n) to break lines:
				- Always insert line break (\\n) after section headers or labels.
				- Use additional line break (\\n) when starting a new logical paragraph or list item.
				- Make sure these line breaks are visible in the final text so they can be rendered correctly when displayed with white-space: pre-line.
			9. Never invent information beyond what is provided by the user message.`,
  )

  return promptParts.join('\n\n')
}

export interface IExamGeneralFeedbackUserPromptState {
  examTitle: string
  messages: IPostExamMessageInternal[]
  notAnsweredLessons: IDynamicPaperTopicLesson[]
  totalLessonsCount: number
  totalMarks: number
  earnedMarks: number
  stopReason?: string
  resultTime?: string
}

export function generateExamGeneralFeedbackUserMessage(
  promptState: IExamGeneralFeedbackUserPromptState,
): string {
  // Splitting into parts to maintain readability while eliminating newline (\n) characters in request payload
  const promptParts: string[] = []

  // Header
  promptParts.push(`Exam title: "${promptState.examTitle}".`)

  // Stop reason
  if (promptState.stopReason) {
    promptParts.push(`Exam stop reason: ${promptState.stopReason}`)
  }

  // Duration
  if (promptState.resultTime) {
    promptParts.push(`Total time spent: ${promptState.resultTime}`)
  }

  // Progress
  const completedCount =
    promptState.totalLessonsCount - promptState.notAnsweredLessons.length
  const progressPercent = Math.round(
    (completedCount / promptState.totalLessonsCount) * 100,
  )
  const marksPercent = Math.round(
    (promptState.earnedMarks / promptState.totalMarks) * 100,
  )

  promptParts.push(
    `Exam progress:
      - Questions completed: ${completedCount}/${promptState.totalLessonsCount} (${progressPercent}%)
      - Total marks earned: ${promptState.earnedMarks}/${promptState.totalMarks} (${marksPercent}%)`,
  )

  // Unanswered lessons
  const unansweredCount = promptState.notAnsweredLessons.length
  promptParts.push(
    `Unanswered questions: ${
      unansweredCount > 0
        ? unansweredCount +
          ' (' +
          promptState.notAnsweredLessons.map((l) => l.title).join(', ') +
          ')'
        : '0'
    }`,
  )

  // Student answers context
  const questionsSummary = promptState.messages
    .filter((msg) => msg.role === PostExamMessageRole.Internal)
    .map((msg, i) => {
      let questionSummary = `
			Question ${i + 1}${msg.content.title ? ` (${msg.content.title})` : ''}:
      - Question: ${msg.content.question || 'N/A'}
      - Student Answers Summary: ${msg.content.studentAnswer || 'N/A'}
      - Tutor Feedback Summary: ${msg.content.tutorFeedback || 'N/A'}`

      if (msg.content.learningDynamics) {
        const {
          initialStruggle,
          studentEngagement,
          hintsUsedCount,
          isAnswerRevealedByTutor,
          isModelSolutionRequested,
          isResolved,
        } = msg.content.learningDynamics

        if (initialStruggle) {
          questionSummary += `
					- Initial Struggle: ${initialStruggle}.`
        }

        if (studentEngagement) {
          questionSummary += `
					- Student Engagement: ${studentEngagement}.`
        }

        if (hintsUsedCount) {
          questionSummary += `
					- Hints Used: ${hintsUsedCount ?? 'N/A'}.`
        }

        if (isAnswerRevealedByTutor) {
          questionSummary += `
					- Answer Revealed by Tutor: Yes.`
        }

        if (isModelSolutionRequested) {
          questionSummary += `
					- Model Solution Requested by Student: Yes.`
        }

        if (isResolved) {
          questionSummary += `
					- Question Resolved by Student: Yes.`
        }
      }

      questionSummary += `
      - Total Marks: ${msg.content.totalMarks || 'N/A'}
      - Earned Marks: ${msg.content.earnedMarks || 'N/A'}`

      return questionSummary
    })
    .join('\n\n')

  promptParts.push(
    `Student submitted answers with tutor evaluations:\n\n${questionsSummary}`,
  )

  // Instructions for GPT
  promptParts.push(
    `Now, using the information above, generate a **comprehensive, structured, and engaging general feedback**. 
    The feedback should:
      - Reflect on progress (answered vs total questions)
      - Mention mark achievements
      - Comment on time management (if time is provided) and exam pacing
      - Consider the stop reason if provided
      - Encourage the student in a friendly, professional tone
      - Be clearly structured with headings, bullet points, and emojis (as appropriate)
      - Avoid inventing any data not provided above.`,
  )

  return promptParts.join('\n\n')
}

export interface IMultiModePromptState {
  question?: string
  questionTextInput?: string
  correctAnswer?: string
  solutionPromptPart?: string
  courseAdditionalInfo?: string // e.g. "Maths, GCSE Higher, Edexcel"
  subMode?: LessonChatSubMode // used for Exam Sub Mode (strict, with timer)
}

export function generateMultiModePrompt({
  question,
  questionTextInput,
  correctAnswer,
  solutionPromptPart,
  courseAdditionalInfo,
  subMode,
}: IMultiModePromptState): string {
  const questionForDisplay = removeBase64Images(question)

  let questionContextPart = `
	1. Question Context:
    - Displayed question: ${questionForDisplay || ''}
    - Correct solution: ${correctAnswer || ''}
    - Request for solution pattern: "${solutionRequestText}"
		${
      courseAdditionalInfo
        ? '- Course Additional Info: ' + courseAdditionalInfo
        : ''
    }
		`

  if (questionTextInput) {
    questionContextPart = `
		1. Question Context:
    - Displayed question: ${questionForDisplay || ''}
    - Question to interpret: ${questionTextInput || ''}
    - Correct solution: ${correctAnswer || ''}
    - Request for solution pattern: "${solutionRequestText || ''}"
		${
      courseAdditionalInfo
        ? '- Course Additional Info: ' + courseAdditionalInfo
        : ''
    }`
  }

  const promptParts = [
    `As an AI Tutor, your task is to provide supportive feedback on student answers while considering their learning journey and previous interactions. Use Return responses in a JSON object containing the 'gptResponseToStudent' field. Your interaction will take place in one of two distinct modes: Assessment Mode or Learning Mode.`,

    `## Tutor Modes Overview:
			- **Assessment Mode:** This mode is primarily focused on **evaluating the student's problem-solving abilities** and **encouraging self-discovery of solutions**. The AI Tutor **must NOT provide the final answer** to the student, except when explicitly requested via exact phrase "${solutionRequestText}". Limited, subtle nudges and directional prompts are provided only if the student demonstrates some understanding and potential for progress. This mode prioritizes assessment over direct teaching.
			- **Learning Mode:** This mode is dedicated to **in-depth, step-by-step teaching and comprehensive explanations** of concepts related to the current lesson's question. The AI Tutor's main goal is to ensure the student **fully grasps the underlying concepts** through guided learning, iterative concept checks, and embedded practice questions. The tutor should **NOT immediately solve the original question** but instead build foundational knowledge gradually. The AI Tutor should guide the student through concepts, check for understanding after each step, and offer small practice questions to confirm mastery. Once the AI Tutor determines that the student has a sufficient grasp of the topic and has successfully completed the guided practice, they should **propose switching back to Assessment Mode** to apply their new knowledge.`,

    `## General Rules for Student Interaction:
			- The student begins a lesson in Assessment Mode.
			- The student can manually switch to Learning Mode at any time.
			- If the student makes multiple incorrect attempts in Assessment Mode, the AI Tutor will propose a switch to Learning Mode.
			- After the student successfully completes the learning path for a topic in Learning Mode, the AI Tutor will propose switching back to Assessment Mode.
			- **Crucially, the context of student answers is tied to the current mode.**
					* When the student provides an answer in **Assessment Mode**, it is always a response to the original lesson question or any follow-up questions asked by the AI Tutor **within Assessment Mode only**.
					* When the student provides an answer in **Learning Mode**, it is always a response to a mini-problem or concept check question asked by the AI Tutor **within Learning Mode only**.
			- The AI Tutor must always evaluate the student's response based on the question relevant to the **current chat mode**, even while considering the full conversation history for context and personalization. The tutor should gently redirect the student if their response appears to be for a question from a different mode.
			- **CRITICAL:** The **"Chat Mode"** tag preceding each message is the definitive source of truth for the interaction context. The AI Tutor's primary task is to interpret and respond to the student's message based on the "Chat Mode" of that specific message, regardless of the conversation history. This tag dictates the rules of engagement (Assessment vs. Learning) for the current student turn. The **'currentChatMode'** field in the AI Tutor's response MUST ALWAYS match the 'currentChatMode' of the incoming user message. The chat mode officially changes only when the student sends a message in the new mode. The AI Tutor's role is to confirm the proposed switch but not to preemptively change the mode itself.`,

    `1. Core Technical Requirements:`,
    questionContextPart,

    `2. Mathematical Expression Formatting:
			- Inline math: Use $...$
				Example: "The value of $x^2$ is..."
			- Block math: Use $$...$$ on **ONE LOGICAL LINE ONLY**.
				Example (CORRECT): "The value of $$x^2$$ is..."
			- Block equations MUST NOT contain real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
			- **RULE FOR MULTI-LINE STRUCTURES (e.g., arrays):** Complex or multi-line LaTeX environments (e.g., \\\\begin{array})**MUST BE** entirely enclosed within $$...$$ and remain on a single logical line, with no real line breaks (\\n) or surrounding 		whitespace inside the $$...$$ delimiters.
				Example (CORRECT): "$$ \\\\begin{array}{r} 37.80\\\\ + 8.97\\\\ \\\\hline \\\\end{array} $$"
			- **All** LaTeX commands must use **DOUBLE** backslashes.
				Example (CORRECT): "$u = 10\\\\frac{m}{s}$"
			- Do NOT use \\( \\), \\[ \\], or any other math delimiters.
			- Use ONLY $...$ for inline math and $$...$$ for block math.
			- Always use double backslashes ("\\\\") instead of single backslashes: \\\\frac, \\\\sqrt, \\\\cdot .
				Example: "$u = 10\\\\frac{m}{s}$" (correct).
			- No single backslashes, no unescaped LaTeX.
			- Avoid: "$u = 10\\frac{m}{s}$" or "$u = 10\ \frac{m}{s}$" (incorrect)
			- Review all KaTeX expressions for proper formatting.
			- If studentAnswerIncludesKaTexSyntax is "true", verify KaTeX syntax in your response.`,

    `3. Currency Notation Rule:
			- For all monetary amounts, prices, or financial calculations, ALWAYS use the text-based currency code (USD, EUR, GBP) or the full currency name (e.g., "dollars") instead of their symbols.`,

    `4. Response Approach:`,
    `**IMPORTANT:** Always remember and consider **all previous messages in the conversation** for context and personalization, regardless of whether they occurred in Assessment Mode or Learning Mode. **However, in Assessment Mode, the primary focus for evaluation MUST be on the original lesson question (${questionForDisplay}) and not on any mini-problems from the Learning Mode conversation history.** This ensures continuity and personalized guidance without confusing the assessment goal.`,

    `5. Guidelines for Assessment Mode:
		This mode is designed to assess student understanding and guide them towards the correct answer **without directly or partially providing it**. Instead, your goal is to:
			- encourage the student to **Find the Answer Themselves**;
			- propose switching to **Learning Mode* if their knowledge needs to be improved or to practice foundational concepts.`,

    ` -**Initial Assessment:**
        * Consider the student's previous attempts and current understanding.
        * Look for partial comprehension and effort.
        * Start with positive acknowledgment of any correct elements.
				* If subject is ${CourseSubject.Maths}, then **All** calculations should be done by student himself. 
			-**Feedback Categories:**
		    * **A. Suggest Learning Mode (Multiple Incorrect Attempts In A Row):** This category has **HIGH PRIORITY**. If the student demonstrates significant conceptual misunderstanding or fundamental gaps in knowledge related to the topic, indicating they are consistently stuck and cannot progress. This also applies if the AI Tutor determines the student needs a deeper foundational understanding or more practice before attempting further assessment. In such cases, **it is crucial to propose switching to Learning Mode**. Response example:
            ` +
      `\`\`\`json
            {
              "gptResponseToStudent": "It looks like we might need to review and practice this topic a bit more. Would you like to switch to Learning Mode to explore this concept in detail?",
              "isChatModeSwitchProposed": true
            }
            \`\`\`
        * **B. Making Progress (Attempted but Incorrect):** When the student's answer shows partial understanding or a good effort, but is not fully correct, indicating they are on the right track but need minor guidance. Response example:
            ` +
      `\`\`\`json
            {
              "gptResponseToStudent": "[**Personalized, encouraging message** that acknowledges correct parts/effort and prompts the student to re-examine their work for small refinements. The tone should be warm and supportive, focusing on encouraging continued effort.]"
            }
            }
            \`\`\`
        * **C. Need Direction (Minor Error In Students Answer/Guidance):** When the student's answer has a minor error or requires a gentle nudge to stay on track, but a full switch to Learning Mode isn't yet necessary. Response example:
            ` +
      `\`\`\`json
            {
              "gptResponseToStudent": "[**Supportive, personalized message** that gently redirects the student by rephrasing the problematic part of their answer with proper notation to highlight the error, not give the solution. Highlights areas for attention or re-emphasizes key concepts.]."
            }
            \`\`\`
        * **D. Off-Topic/Clarification Needed (Unrelated Answer):** When the student's response is entirely unrelated to the current question or topic, indicating a complete lack of understanding of what is being asked or a significant distraction. Response example:
            ` +
      `\`\`\`json
            {
              "gptResponseToStudent": "Thanks for your response, but it seems we might be a bit off-topic. Let's get back to the current question. The problem is asking us to [rephrase the question clearly and concisely, potentially adding more detail or context]."
            }
            \`\`\`
    	- **Student Requests:**
        * **Hints:** Only provide a hint if the student explicitly asks for one. Student has score penalty in Assessment Mode when hint is directly requested. Use hints sequentially, building on the student's current understanding and connecting hints to their previous attempts.
        * **Reveal Answer & Reasoning:** Only reveal the correct solution and reasoning if the student explicitly asks using the exact phrase: "${solutionRequestText}".
			-**Guiding Questions:** Use clarifying and guiding questions to help the student think critically.
			-**Error Handling:** Treat ambiguous answers as opportunities for clarification. Ask about the student's approach when unclear.
			-**Mode Transition Rules:**	Upon switching from Learning Mode to Assessment Mode, the AI Tutor MUST reset its focus to the original lesson question: "${questionForDisplay}".All previous mini-problems or practice questions from Learning Mode are now considered part of the conversation history, but they are NO LONGER the current question to be evaluated.
    `,

    `6. Guidelines for Learning Mode:
    This mode is designed for **in-depth teaching, step-by-step concept explanation, and guided practice** related to the current lesson's question. The primary goal is to ensure the student **fully grasps the underlying concepts** and can independently approach similar problems, rather than immediately solving the initial question for them.

			-**Guided Learning Path (Step-by-Step Approach):**
        * **Initial Response:** Do NOT reveal the solution to the original question. Instead, start by introducing the core concept(s) required to solve the problem. Explain these concepts clearly and concisely.
        * **Concept Check & Next Step:** After explaining a concept, always include a **follow-up question** to check understanding and guide the next step. This could be:
            * "How would you apply [this concept] to a simple example?" (e.g., "How would you apply the order of operations to $2+3 \\times 4$?")"
            * "Are you ready to move on to the next concept, or should we explore this one more?"
            * "What part of [this concept] would you like me to clarify?"
        * **Iterative Teaching:** Based on the student's response to your concept check, either:
        			- **Clarify/Re-explain:** If the student struggles, re-explain the concept in a different way, provide more examples, or break it down further.
							- **Move to Next Concept/Step:** If the student understands, introduce the next logical concept or step required to eventually solve the original question. Always ensure to stay within the scope of the original question and its related topic.
        * **Practice Questions (Embedded):** Once all necessary concepts are explained and confirmed understood, generate a **small, guiding practice question** that is *similar in concept* to the main lesson question (a "mini-problem"). This is to assess their practical application of the learned concepts, NOT to immediately solve the original question. Evaluate their answer to this mini-problem.
				* **Completion and Transition:** When the student has successfully demonstrated a strong grasp of all concepts and completed the guided practice for the current topic, **the tutor MUST propose switching back to Assessment Mode** to allow the student to apply their learned knowledge to the original question (Question to Display). **The message proposing the switch MUST NOT contain any questions or tasks for the student.** Do **NOT** introduce new topics or move beyond the scope of the initial question.
				* **Final Message in Learning Mode:** The final message that proposes a switch **must be a clear and concise summary of the learning achieved**, followed by the proposal to switch. This message should not contain any new questions. The "isChatModeSwitchProposed" flag must be set to "true" to signal the front-end to enable the transition.
				* **Enhanced Feedback (Optional):** When providing feedback in Learning Mode, the tutor may optionally include "commonMistake" and "tip" fields if they are relevant to the concept being taught:
							- **commonMistake:** Describe a typical misconception related to the student's incorrect step.
							- **tip:** Offer a helpful strategy or mnemonic for similar problems.

			-**Quick Response Prompts:**
        * An optional array of 1-3 short, clickable response options (max 5-7 words each) that serve as **direct, rapid replies to the AI Tutor's *last message***. These buttons are designed to streamline interaction by allowing the student to quickly confirm understanding, request specific clarification on a part of the *current explanation*, or ask for the *next logical piece of information* that the tutor can immediately provide.
        * **Crucially, NEVER INCLUDE** options that:
            * **Prompt the student to ask a question** (e.g., "Ask a question", "I have a question"). Students can type freely.
            * **Ask the student to perform a calculation or task outside the chat** (e.g., "Multiply 9 by 9", "Solve this"). These are for the student to type.
            * **Imply a skipped step or unconfirmed understanding** (e.g., "Next step", "I understand", "Next topic"). Confirmation must come from student input.
            * **Assume student action or knowledge not explicitly stated** (e.g., "You correctly worked out that...").
        * Instead, quick responses should offer the student ways to:
            * **Request an example** based on the current explanation (e.g., "Give an example of [concept]").
            * **Request more detail/re-explanation** on a specific part of the *last message's content* (e.g., "Explain [term] more").
            * **Indicate they're ready to proceed to the *tutor's next teaching point* IF the current step's understanding has been confirmed by *their own explicit answer***. (e.g., "Ready for [next concept]").
            * **Request a small, guiding practice question** on the *just-explained concept*.
						* **Dynamic Transition Prompt:** When the student has successfully demonstrated a strong grasp of the current topic and is nearing the end of the Learning Mode flow, the AI Tutor **MUST** include a prompt that suggests switching back to Assessment Mode to apply their new knowledge. This button should be prioritized. The text should be clear and concise (e.g., "All clear", "Ready for Assessment Mode").
        * **Examples of appropriate prompts:** "Explain [concept] again", "Give an example", "Practice this concept", "Clarify [specific part]".
    `,

    `7. Solution Response Approach:
			- If the last student message is exactly "${solutionRequestText}", then **focus solely on the step-by-step solution**:
			- Do **not** provide general feedback or hints in this specific response.
			- Only include the structured solution breakdown.
			- Keep in mind that a request for a solution usually means the student doesn't know how to answer the question.
		`,
  ]

  // Append solution prompt if provided
  if (solutionPromptPart) {
    promptParts.push(
      ...[
        `## Solution Response Guidelines:`,

        `**Solution Response Structure:**
				When providing a solution (only when the last student message is "${solutionRequestText}"),
				include these fields in the JSON response:
					` +
          `\`\`\`json
					{
						"gptResponseToStudent": "[Step-by-step explanation including the final answer]",
						"commonMistake": "[Description of common errors students make with this problem type]",
						"tip": "[Helpful strategy or shortcut for similar problems]",
						"isSolutionResponse": true
					}
					\`\`\`
        `,

        `**Field Usage Guidelines for Solution Response:**`,
        `1.  **gptResponseToStudent** - REQUIRED field for all solution responses:
				    -   Must contain the **complete, step-by-step breakdown of how to solve the problem and show the final answer.**
						-   The explanation should be clear, detailed, and follow all specified formatting rules.`,

        `2.  **commonMistake** - Include when:
						-   There's a typical misconception worth highlighting.
						-   The error pattern is common for this type of problem.
						-   It helps prevent future mistakes.
						-   Format: "Students often [describe error] because [reason]. Remember that [correction]."`,

        `3.  **tip** - Include when:
						-   There's a reusable problem-solving strategy.
						-   A particular approach simplifies the solution.
						-   It helps generalize the learning.
						-   Format: "For similar problems, try [strategy] because [reason]."`,

        `**Solution Response Structure and Formatting rules:**`,
        `${solutionPromptPart}`, // solution prompt part based on course subject specification
      ],
    )
  }

  // add general reminders
  promptParts.push(
    ...[
      `**Remember**:
				- Maintain an encouraging tone throughout the conversation.
				- Guide rather than tell.
				- Focus on the learning process.
				- Keep technical requirements intact.
				- **Always consider the full conversation history to maintain context and provide personalized guidance.**
		`,
    ],
  )

  const prompt = promptParts.join('\n')

  return prompt
}

export function isVimeoUrl(url: string): boolean {
  var pattern = new RegExp('^(http|https)://(www.)?vimeo.com/([0-9])+$')
  return pattern.test(url)
}

export function convertToEmbedUrl(url?: string): string | undefined {
  if (!url) {
    return undefined
  }

  if (!isVimeoUrl(url)) {
    return url
  }

  const videoId = url.split('/').pop()
  return `https://player.vimeo.com/video/${videoId}`
}

export function handleKeyDown(e: React.KeyboardEvent) {
  e.preventDefault()
}

export function calculateElo(
  studentElo: number,
  opponentElo: number,
  outcome: number,
) {
  const r = 0.05
  const expectedScore = calculateExpectedOutcome(studentElo, opponentElo)
  // This is a DELTA (K * (outcome - expected)), not an absolute Elo -
  // callers add it to the current MMR (Pavlo review, 9 Sep: the old name
  // newStudentElo read as an absolute score and collided with real ones).
  const studentEloDelta = K_FACTOR * (outcome - expectedScore)
  const newOpponentElo = opponentElo - r * K_FACTOR * (outcome - expectedScore)

  // studentEloDelta can be a negative number, be careful with the calculations
  return [studentEloDelta, newOpponentElo]
}

export function beautifyLatexString(latex: string) {
  return latex.replace('\\iint_', '\\int_')
}

/**
 * Finds the index of the last message with role 'system' in the conversation array.
 * @param {Array} conversation - Array of conversation messages.
 * @returns {number} - The index of the last 'system' message, or -1 if not found.
 */
export function getLastSystemMessageIndex(
  conversation: THistoryMessage[],
): number {
  return conversation
    .slice()
    .reverse()
    .findIndex((mes) => mes.role === MessageRole.System)
}

function transformConversationData(
  jsonData: THistoryMessage[],
): THistoryMessage[] {
  try {
    // Validate input
    if (!Array.isArray(jsonData)) {
      throw new Error('Input must be an array')
    }

    // Initialize result array
    const result: THistoryMessage[] = []

    // First, find and process system messages
    const systemMessages = jsonData.filter(
      (item) => item.role === MessageRole.System,
    )
    if (systemMessages.length > 0) {
      // Get first system message
      const systemMessage: THistoryMessage = {
        ...systemMessages[systemMessages.length - 1], // Keep all system message properties
        role: MessageRole.System,
      }
      result.push(systemMessage)
    }

    // Then process user and assistant messages in order, keeping all content
    const userAssistantMessages = jsonData
      .filter(
        (item) =>
          item.role === MessageRole.User ||
          item.role === MessageRole.Assistant ||
          item.role === MessageRole.Variant ||
          item.role === MessageRole.Question,
      )
      .map((item) => {
        // Keep all properties for both user and assistant messages
        const message: THistoryMessage = {
          ...item,
        }
        return message
      })

    // Combine all messages
    result.push(...userAssistantMessages)

    return result
  } catch (error) {
    console.error('Error transforming conversation data:', error)
    throw error
  }
}

/**
 * Filters the conversation to include only messages after and including the last 'system' message.
 * @param {Array} conversation - Array of conversation messages.
 * @returns {Array} - Filtered conversation array.
 */
export function filterConversationFromLastSystem(
  conversation: THistoryMessage[],
): THistoryMessage[] {
  const lastSystemMessageIndex = getLastSystemMessageIndex(conversation)
  const systemMessageIndex =
    lastSystemMessageIndex === -1
      ? 0
      : conversation.length - 1 - lastSystemMessageIndex

  // Include the system message by adding 1 to systemMessageIndex
  return conversation.slice(systemMessageIndex)
}

// filter messages with imageUrls, keeping only the last one
export function filterMessagesWithImageUrls(
  conversation: THistoryMessage[],
): THistoryMessage[] {
  // Find the index of the last message with imageUrls
  const lastMessageWithImageUrlsIndex = conversation.reduce(
    (lastIndex, message, index) => {
      return message.imageUrls && message.imageUrls.length ? index : lastIndex
    },
    -1,
  )

  // Filter messages with imageUrls except the last one
  return conversation.filter((message, index) => {
    if (
      index !== lastMessageWithImageUrlsIndex &&
      message.imageUrls &&
      message.imageUrls.length
    ) {
      return false
    }

    return true
  })
}

export function updateMMRHistory(
  lessonContentId: string,
  mmrHistory: LineChartNumberDataset | undefined,
  currentMmr: number,
  studentEloDelta: number,
  hint = false,
  setFunc: (mmrHistory: LineChartNumberDataset) => void,
) {
  const currentData = mmrHistory || {
    labels: ['Start'],
    datasets: [{ data: [currentMmr] }],
  }
  const newLabel = hint ? `hint_${lessonContentId}` : lessonContentId
  const currentDataset = currentData.datasets[0].data

  setFunc({
    labels: [...currentData.labels, newLabel],
    datasets: [
      {
        label: 'ELO',
        data: [...currentDataset, studentEloDelta],
        fill: false,
        borderColor: 'rgb(33, 144, 255)',
        backgroundColor: 'rgba(33, 144, 255, 1)',
        borderWidth: 2,
      },
    ],
  })
}

export function getPointsFromGpt(gptResponse: IGptResponseArguments): number[] {
  let points = [0, 0]

  if (gptResponse.points) {
    try {
      const newPoints = gptResponse.points.split('/').map((p) => +p)

      if (newPoints.length === 2 && newPoints.every((p) => !isNaN(p))) {
        points = newPoints
      }
    } catch (error) {
      console.error('getPointsFromGpt', { error, points: gptResponse.points })

      return points
    }
  }

  return points
}

export const customVariantIdIdentifier = 'CUSTOM_ID'

export function generateCustomId(): string {
  return `${customVariantIdIdentifier}-${
    Math.floor(Math.random() * 9000) + 1000
  }`
}

export function generateOpenTextGptConversation(
  messages: THistoryMessageOpenText[],
  isOnboardingFlow: boolean = false,
  useCurrentChatMode = false,
): IGptConversationMessage[] {
  // Use utility function to filter conversation from the last system message
  let filteredConversation = transformConversationData(messages)

  // Further filter messages with imageUrls, keeping only the last one
  if (!isOnboardingFlow) {
    filteredConversation = filterMessagesWithImageUrls(filteredConversation)
  }

  let questionNo = 1

  // generate conversation for gpt and return
  return filteredConversation.reduce(
    (acc: IGptConversationMessage[], message) => {
      // Handle system role
      if (message.role === MessageRole.System) {
        let content = message.content as string

        if (
          content &&
          typeof content === 'string' &&
          content.includes('base64')
        ) {
          // handles broken history messages (saved with base64 images) causing gpt response fail. base64 will be replaced for empty string
          content = removeBase64Images(content) || ''
        }

        return [...acc, { content, role: message.role }]
      }

      // Handle question/variant role
      if (
        message.role === MessageRole.Question ||
        message.role === MessageRole.Variant
      ) {
        const content = message.content as TMessageContentObject
        // Validate question content structure
        if (!content || !content.question) {
          throw new Error('Question items must have content.question property')
        }

        // handles broken history messages (saved with base64 images) causing gpt response fail. base64 will be replaced for empty string
        const question =
          typeof content.question === 'string' &&
          content.question.includes('base64')
            ? removeBase64Images(content.question)
            : content.question

        let textContent = `Question ${questionNo}:\n${question}`

        // add current chat mode flag
        if (useCurrentChatMode && message.currentChatMode) {
          textContent = `Chat Mode: ${message.currentChatMode},\n\nQuestion ${questionNo}:\n${question}`
        }

        questionNo += 1

        return [
          ...acc,
          {
            role: MessageRole.Assistant,
            content: textContent,
          },
        ]
      }

      // Assistant message update used to prevent undefined content in message that
      // will throw error from gpt and response would not be provided
      if (message.role === MessageRole.Assistant) {
        let contentForGpt = ''

        // this case could be potentially reproduced when error text saved as assistant message
        if (typeof message.content === 'string' && !!message.content) {
          contentForGpt = message.content
        }

        // handle case when content type is IGptResponseArguments
        if (
          message.content &&
          typeof message.content === 'object' &&
          Object.keys(message.content).includes('gptResponseToStudent')
        ) {
          try {
            const {
              gptResponseToStudent,
              commonMistake,
              tip,
              marksBreakdown,
              actualMarks,
              totalMarks,
            } = message.content as IGptResponseArguments
            contentForGpt = gptResponseToStudent

            if (commonMistake) {
              contentForGpt += `\n\nCommon Mistake:\n${commonMistake}`
            }

            if (tip) {
              contentForGpt += `\n\nTip:\n${tip}`
            }

            if (actualMarks && totalMarks) {
              contentForGpt += `\n\nMarks Awarded: ${actualMarks}/${totalMarks}`
            }

            if (marksBreakdown) {
              contentForGpt += `\n\nMarks Breakdown:\n${marksBreakdown}`
            }
          } catch (error) {
            console.error('Error processing gptResponseToStudent:', error)
          }
        }

        // handle case when content type is TMessageContentObject
        if (
          message.content &&
          typeof message.content === 'object' &&
          Object.keys(message.content).includes('tutorFeedback')
        ) {
          const content = message.content as TMessageContentObject

          if (content.tutorFeedback) {
            contentForGpt = `${content.correctAnswer}`
          }

          if (content.commonMistake) {
            contentForGpt += `\n\nCommon Mistake:\n${content.commonMistake}`
          }

          if (content.tip) {
            contentForGpt += `\n\nTip:\n${content.tip}`
          }
        }

        // add message only if content is presented, empty content could lead to infinite next response errors
        if (contentForGpt) {
          return [
            ...acc,
            {
              role: message.role,
              content: contentForGpt,
            },
          ]
        }
      }

      // handle image upload by student
      // message content should be changed to array of parameters for gtp conversation
      if (message.role === MessageRole.User) {
        let textContent = message.content as string
        const content: TContentFileUpload = []

        // add regular text part if presented
        if (textContent && typeof textContent === 'string') {
          // Multi-line MathLive working is stored as one
          // `$ \displaylines{...} $` blob (the editor needs it to rehydrate
          // the field, so the stored value stays). Sent to the marker as-is,
          // the model annotates the whole macro as a single truncated
          // "line" and both the line-by-line marking and the Ask-Axi seed
          // lose the actual working - split it into real lines here.
          textContent = displayLinesToSeparateLines(textContent)
          if (useCurrentChatMode && message.currentChatMode) {
            textContent = `Chat Mode: ${message.currentChatMode},\n\nMessage:\n${textContent}`
          }

          content.push({
            type: ContentFileUploadType.Text,
            text: textContent,
          })
        }

        // add images content parts
        if (!!message.imageUrls?.length) {
          for (const url of message.imageUrls) {
            const fileUploadContent: IContentFileUploadImage = {
              type: ContentFileUploadType.ImageUrl,
              image_url: { url },
            }

            !!url && content.push(fileUploadContent)
          }
        }

        return [
          ...acc,
          {
            role: message.role,
            content,
          },
        ]
      }

      return acc
    },
    [],
  )
}

export const kaTexOperators = [
  '\\frac',
  '\\sqrt',
  '\\nthroot',
  '\\sum',
  '\\prod',
  '\\int',
  '\\lim',
  '\\sin',
  '\\cos',
  '\\tan',
  '\\cot',
  '\\sec',
  '\\csc',
  '\\log',
  '\\ln',
  '\\degree',
  '\\pm',
  '\\times',
  '\\div',
  '\\neq',
  '\\leq',
  '\\geq',
  '\\lt',
  '\\gt',
  '\\approx',
  '\\infty',
  '\\subset',
  '\\supset',
  '\\cup',
  '\\cap',
  '\\theta',
  '\\alpha',
  '\\beta',
  '\\gamma',
  '\\delta',
  '\\epsilon',
  '\\zeta',
  '\\eta',
  '\\theta',
  '\\iota',
  '\\kappa',
  '\\lambda',
  '\\mu',
  '\\nu',
  '\\xi',
  '\\omicron',
  '\\pi',
  '\\rho',
  '\\sigma',
  '\\tau',
  '\\upsilon',
  '\\phi',
  '\\chi',
  '\\psi',
  '\\omega',
  '\\Gamma',
  '\\Delta',
  '\\Theta',
  '\\Lambda',
  '\\Xi',
  '\\Pi',
  '\\Sigma',
  '\\Upsilon',
  '\\Phi',
  '\\Psi',
  '\\Omega',
  '\\arcsin',
  '\\arccos',
  '\\arctan',
  '\\arccot',
  '\\arcsec',
  '\\arccsc',
  '\\matrix',
  '\\pmatrix',
  '\\bordermatrix',
  '\\cases',
  '\\langle',
  '\\rangle',
  '\\lceil',
  '\\rceil',
  '\\lfloor',
  '\\rfloor',
  '\\vert',
  '\\Vert',
  '\\binom',
  '\\partial',
  '\\nabla',
  '\\emptyset',
  '\\in',
  '\\notin',
  '\\subsetneq',
  '\\subseteqq',
  '\\supsetneq',
  '\\supseteqq',
  '\\therefore',
  '\\because',
  '\\propto',
  '\\cong',
  '\\nless',
  '\\ngtr',
  '\\left',
  '\\right',
  '\\dfrac',
  '\\cdot',
  '\\text',
]

function wrapKaTexOperator(text: string): string {
  if (typeof text !== 'string') {
    return ` ${text}`
  }

  try {
    for (const operator of kaTexOperators) {
      if (text.includes(operator)) {
        return ` $${text}$`
      }
    }
  } catch (error) {
    console.error('Error in wrapKaTexOperator:', error)
  }

  return ` ${text}`
}

export function extractAnswerAsText(jsonObj: any): string {
  let text = ''

  if (jsonObj instanceof Object) {
    if (jsonObj.hasOwnProperty('text')) {
      text += wrapKaTexOperator(jsonObj.text)
    }

    if (jsonObj.hasOwnProperty('equation')) {
      text += wrapKaTexOperator(jsonObj.equation)
    }

    if (jsonObj.hasOwnProperty('children') && Array.isArray(jsonObj.children)) {
      for (let child of jsonObj.children) {
        let childText = extractAnswerAsText(child)
        if (typeof childText === 'string' && childText.includes('Hint')) {
          return text // Stop adding children if 'Hint' is encountered
        }
        text += ` ${childText}`
      }
    }
  }

  const plainAnswer = text.replace('Answer text:', '')

  return plainAnswer
}

export function getHint(
  hints: string | string[],
  usedHints: string[],
): string | undefined {
  if (typeof hints === 'string') {
    return hints
  }

  let nextHint: string | undefined = undefined

  for (const hint of hints) {
    if (!usedHints.includes(hint)) {
      nextHint = hint

      break
    }
  }

  return nextHint
}

export function getMultipleSelectStudentsSavedAnswersFromHistory(
  history: IHistoryItemMultipleSelect,
): IMultipleSelectValue[] | undefined {
  const { messages, lastVariant } = history

  if (!messages) {
    return undefined
  }

  if (!lastVariant) {
    return
  }

  // get messages related to last variant
  const studentAnswersMessagesReversed = messages
    .filter(
      (m) =>
        m.role === MessageRole.User &&
        m.currentVariantId === lastVariant.id &&
        m.content.studentsAnswers,
    )
    .reverse()

  // get last student answer message
  const lastStudentMessageWithAnswer:
    | THistoryMessageMultipleSelect
    | undefined = studentAnswersMessagesReversed.length
    ? studentAnswersMessagesReversed[0]
    : undefined

  // get student answers
  const studentsAnswers: IMultipleSelectValue[] | undefined =
    lastStudentMessageWithAnswer?.content.studentsAnswers

  return studentsAnswers
}

export function generateMatchingLinesValuesFromConnections(
  initialValues: IMatchingLinesValue[],
  connections: IMatchingLinesConnection[],
): IMatchingLinesValue[] {
  let values: IMatchingLinesValue[] = []

  for (const connection of connections) {
    const targetValue: IMatchingLinesValue = {
      left: initialValues[connection.fromId].left,
      right: initialValues[connection.toId].right,
    }
    values = [...values, targetValue]
  }

  return values
}

export function generateMatchingLinesConnectionsFromValues(
  initialValues: IMatchingLinesValue[],
  values: IMatchingLinesValue[],
): IMatchingLinesConnection[] {
  let connections: IMatchingLinesConnection[] = []

  for (const value of values) {
    const fromId = initialValues.findIndex(
      (initialValue) => initialValue.left === value.left,
    )
    const toId = initialValues.findIndex(
      (initialValue) => initialValue.right === value.right,
    )

    const connection: IMatchingLinesConnection = {
      fromId: fromId,
      toId: toId,
    }

    connections = [...connections, connection]
  }

  return connections
}

// Simple hash function for strings
function hashString(str: string): number {
  let hash = 0
  if (str.length === 0) return hash
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert string to 32-bit integer
  }
  return hash
}

// create hash from string and compare hashes, used for better performance, because of left and right values structure (json stringified)
export function checkIfMatchingLinesValuesArraysAreSame(
  arr1: IMatchingLinesValue[],
  arr2: IMatchingLinesValue[],
): boolean {
  if (arr1.length !== arr2.length) {
    return false
  }

  // Create a hash map to count occurrences of each key in the first array
  const map = new Map<number, number>()

  // Function to create a hash key from an IMatchingLinesValue object
  const getHashKey = (item: IMatchingLinesValue): number =>
    hashString(`${item.left}|${item.right}`)

  // Fill the map with elements from the first array
  for (const item of arr1) {
    const hashKey = getHashKey(item)
    map.set(hashKey, (map.get(hashKey) || 0) + 1)
  }

  // Check elements from the second array against the map
  for (const item of arr2) {
    const hashKey = getHashKey(item)
    if (!map.has(hashKey)) {
      return false
    }
    const count = map.get(hashKey)!
    if (count === 1) {
      map.delete(hashKey)
    } else {
      map.set(hashKey, count - 1)
    }
  }

  // If the map is empty, the arrays match
  return map.size === 0
}

export function generateMatchingLinesLastVariantWithLeftAndRightSideItems(
  initialVariant: ILessonVariantMatchingLines,
): ILessonVariantMatchingLines {
  const shuffledValues: IMatchingLinesValue[] = shuffleArray(
    initialVariant.values,
  )

  const valuesWithIds = shuffledValues.map((item, i) => ({
    ...item,
    id: i,
  }))

  const leftSideItems = valuesWithIds.map((el) => ({
    value: el.left,
    id: el.id,
  }))

  const rightSideItems = valuesWithIds.map((el) => ({
    value: el.right,
    id: el.id,
  }))

  const updatedVariant: ILessonVariantMatchingLines = {
    ...initialVariant,
    values: shuffledValues,
    leftSideItems: shuffleArray(leftSideItems),
    rightSideItems: shuffleArray(rightSideItems),
  }

  return updatedVariant
}

export async function findMorePracticeVariant(
  topicName: string,
  lessonTitle: string,
  variantMmr: number,
  usedVariantsIds: string[] = [],
): Promise<IPracticeQuestion | undefined> {
  try {
    const practiceQuestionsVariants: IPracticeQuestion[] =
      await getPracticeQuestions(topicName, lessonTitle, variantMmr)

    let targetVariant: IPracticeQuestion | undefined = undefined

    for (const variant of practiceQuestionsVariants) {
      if (!usedVariantsIds.includes(variant.id)) {
        targetVariant = variant
      }
    }

    return targetVariant
  } catch (error: unknown) {
    console.error(
      'error findMorePracticeVariant :>> ',
      (error as IGenericError)?.message || error,
    )

    if (error && typeof error === 'object' && 'message' in error) {
      notify('error', error.message)
    }

    return undefined
  }
}

// practiceQuestion document has hints field and it is of type string.
// sometimes it is raw string, and sometimes it is JSON stringified array of strings
// we should handle all cases and return array of strings or undefined to generate HistoryLastVariant
export function extractHintsFromPracticeQuestion(
  hints: string[] | string | null | undefined,
): string[] | undefined {
  if (!hints) {
    return undefined
  }

  if (isArray(hints)) {
    return hints
  }

  if (typeof hints !== 'string') {
    // in case of broken data in DB
    return undefined
  }

  // try to parse string and check if it is an array type
  let hintsArray: string[] | undefined = safeJsonParse<string[]>(hints)

  if (isArray(hintsArray) && hintsArray.length) {
    // return array if isArray true and array nt empty
    return hintsArray
  }

  // case when hints is string and not JSON.stringified array, return array with single value
  return [hints]
}

export const correctAnswerTutorResponseText = 'Well done.'

export const provideSolutionTutorResponseText = 'Here is the solution:'

export const completeLessonAndProvideSolutionTutorResponseText = `This question is now complete. ${provideSolutionTutorResponseText}`

export const nowTryThisQuestionText = 'Now try this question:'

export const initialLearningChatModeMessageText = `Ok, let's learn`

export const gptErrorMessage =
  'Sorry, we are unable to explain at the moment due to technical issues.'
export const gptErrorMessageStreaming =
  'I apologize, but I encountered an error while processing your request. Please try again.'

// double backslashes are used to beautify text in editor component,
// that is used to visualize text provided by student
export const hintRequestText = 'Give\\ me\\ a\\ hint'
export const solutionRequestText =
  'Show\\ me\\ the\\ answer\\ and\\ walk\\ me\\ through\\ the\\ reasoning'
export const modelSolutionRequestText =
  'Please\\ show\\ me\\ a\\ model\\ solution\\ for\\ this\\ question'
export const generalFeedbackRequestText =
  'Please\\ provide\\ general\\ feedback\\ on\\ my\\ answer\\ based\\ on\\ the\\ mark\\ scheme'
export const nextVariantRequestText = `I'd\\ like\\ to\\ try\\ a\\ more\\ difficult\\ question`

export function addCorrectSolutionMessageToOpenTextHistory<
  T extends { lastVariant: TLastVariantOpenText },
>(
  currentVariant: TLastVariantOpenText,
  history: T,
  messages: THistoryMessageOpenText[],
  attemptCount: number,
  givenHintCount: number,
  additionalText: string | undefined = undefined,
  explanation: string,
  shouldRemoveLastGptResponseMessage = false,
  keepHasNextVariantValue = false,
  additionalMessageParts: {
    tip?: string
    commonMistake?: string
  } | null = null,
): {
  updatedHistory: T
  messagesToSave: THistoryMessageOpenText[]
} {
  let updatedHistory: T = { ...history }
  let updatedMessages: THistoryMessageOpenText[] = [...messages]

  // generate tutor feedback
  const tutorFeedback: IHistoryMessageContentObjectTutorFeedback = {
    status: StudentAnswerStatus.InCorrect, // change status to incorrect to close question
  }

  if (additionalText) {
    tutorFeedback.additionalText = additionalText
  }

  // generate tutor message content
  const correctAnswerMessageContentObj: TMessageContentObject = {
    correctAnswer: String(explanation),
    variantId: currentVariant.id,
    question: currentVariant.question,
    tutorFeedback,
  }

  // add additional message fields (used on solution response)
  if (additionalMessageParts?.tip) {
    correctAnswerMessageContentObj.tip = additionalMessageParts.tip
  }

  if (additionalMessageParts?.commonMistake) {
    correctAnswerMessageContentObj.commonMistake =
      additionalMessageParts.commonMistake
  }

  // generate tutor message with correct answer
  const correctAnswerMessage: THistoryMessageOpenText = {
    content: correctAnswerMessageContentObj,
    role: MessageRole.Assistant,
    currentVariantId: currentVariant.id,
    isAnswerProvidedByTutor: true,
    attemptCount,
    givenHintCount,
  }

  if (shouldRemoveLastGptResponseMessage) {
    // remove gpt response message and replace it with correct answer message
    updatedMessages.splice(-1)
  }

  // update messages
  updatedMessages = [...updatedMessages, correctAnswerMessage]

  // update history
  updatedHistory = {
    ...updatedHistory,
    messages: updatedMessages,
    conversationFlowState: OpenTextLessonConversationFlowState.Completed,
    lastVariant: {
      ...updatedHistory.lastVariant,
      hasNextVariant: keepHasNextVariantValue
        ? updatedHistory.lastVariant.hasNextVariant
        : false,
      answerRevealed: false,
    },
    isLastVariantCompleted: true,
  }

  if (updatedHistory.lastVariant.hasMorePracticeVariant) {
    // show more practice btn
    updatedHistory = {
      ...updatedHistory,
      conversationFlowState: OpenTextLessonConversationFlowState.MorePractice,
      isLessonCompleted: false,
    }
  }

  return {
    updatedHistory,
    messagesToSave: [correctAnswerMessage],
  }
}

export function addCorrectSolutionMessageToMultiModeChatOpenTextHistory<
  T extends {
    lastVariant: TLastVariantOpenText
    currentChatMode: LessonChatMode
  },
>(
  currentVariant: TLastVariantOpenText,
  history: T,
  messages: TMultiModeChatHistoryMessageOpenText[],
  attemptCount: number,
  givenHintCount: number,
  additionalText: string | null = null,
  explanation: string,
  shouldRemoveLastGptResponseMessage = false,
  keepHasNextVariantValue = false,
  additionalMessageParts: {
    tip?: string
    commonMistake?: string
  } | null = null,
): {
  updatedHistory: T
  messagesToSave: TMultiModeChatHistoryMessageOpenText[]
} {
  let updatedHistory: T = { ...history }
  let updatedMessages: TMultiModeChatHistoryMessageOpenText[] = [...messages]

  // generate tutor feedback
  const tutorFeedback: IHistoryMessageContentObjectTutorFeedback = {
    status: StudentAnswerStatus.InCorrect, // change status to incorrect to close question
  }

  if (additionalText) {
    tutorFeedback.additionalText = additionalText
  }

  // generate tutor message content
  const correctAnswerMessageContentObj: TMessageContentObject = {
    correctAnswer: String(explanation),
    variantId: currentVariant.id,
    question: currentVariant.question,
    tutorFeedback,
  }

  // add additional message fields (used on solution response)
  if (additionalMessageParts?.tip) {
    correctAnswerMessageContentObj.tip = additionalMessageParts.tip
  }

  if (additionalMessageParts?.commonMistake) {
    correctAnswerMessageContentObj.commonMistake =
      additionalMessageParts.commonMistake
  }

  // generate tutor message with correct answer
  const correctAnswerMessage: TMultiModeChatHistoryMessageOpenText = {
    content: correctAnswerMessageContentObj,
    role: MessageRole.Assistant,
    currentVariantId: currentVariant.id,
    isAnswerProvidedByTutor: true,
    attemptCount,
    givenHintCount,
    currentChatMode: updatedHistory.currentChatMode,
  }

  if (shouldRemoveLastGptResponseMessage) {
    // remove gpt response message and replace it with correct answer message
    updatedMessages.splice(-1)
  }

  // update messages
  updatedMessages = [...updatedMessages, correctAnswerMessage]

  // update history
  updatedHistory = {
    ...updatedHistory,
    messages: updatedMessages,
    conversationFlowState: OpenTextLessonConversationFlowState.Completed,
    lastVariant: {
      ...updatedHistory.lastVariant,
      hasNextVariant: keepHasNextVariantValue
        ? updatedHistory.lastVariant.hasNextVariant
        : false,
      answerRevealed: false,
    },
    isLastVariantCompleted: true,
  }

  if (updatedHistory.lastVariant.hasMorePracticeVariant) {
    // show more practice btn
    updatedHistory = {
      ...updatedHistory,
      conversationFlowState: OpenTextLessonConversationFlowState.MorePractice,
      isLessonCompleted: false,
    }
  }

  return {
    updatedHistory,
    messagesToSave: [correctAnswerMessage],
  }
}

export function addNextVariantMessageToOpenTextHistory(
  lessonContent: ILessonContentOpenText,
  nextVariant: TLastVariantOpenText,
  history: IHistoryItemOpenTextFulfilled,
  solutionPromptPart: string | undefined = undefined,
  themeMMRs?: {
    [themeId: string]: number
  },
): {
  updatedHistory: IHistoryItemOpenTextFulfilled
  messagesToSave: THistoryMessageOpenText[]
} {
  let updatedHistory: IHistoryItemOpenTextFulfilled = { ...history }

  // generate variant message content
  const messageContent: TMessageContentObject = {
    question: nextVariant.question,
    correctAnswer: nextVariant.correctAnswer,
    variantId: nextVariant.id,
    isNextVariant: true,
  }

  // generate variant message
  const variantMessage: THistoryMessageOpenText = {
    role: MessageRole.Variant,
    content: messageContent,
    isQuestion: false,
    currentVariantId: nextVariant.id,
    attemptCount: 0,
    givenHintCount: 0,
  }

  // update used variants ids
  const updatedUsedVariantsIds: string[] = [
    ...(updatedHistory.usedVariantsIds || []),
    nextVariant.id,
  ]

  // generate last variant
  const lastVariant: TLastVariantOpenText = {
    ...nextVariant,
    hintsCount: nextVariant.hints?.length || 0,
    isAnswered: false,
    attemptCount: 0,
    givenHintCount: 0,
    answerRevealed: false,
    isAnsweredCorrectly: false,
    isLastQuestionCompleted: false,
  }

  const themeMmr = lessonContent.themeId
    ? themeMMRs?.[lessonContent.themeId]
    : undefined
  const studentMmr = themeMmr || DEFAULT_MMR

  // find next not answered variant
  const hasNextVariant = !!findNextVariant(
    studentMmr,
    lessonContent.variants,
    updatedUsedVariantsIds,
  )

  // update hasNextVariant if there not answered harder variant found
  if (hasNextVariant) {
    lastVariant.hasNextVariant = true
  }

  // generate prompt system message for next variant
  const promptState: IPromptState = {
    question: nextVariant.question,
    questionTextInput: nextVariant.questionTextInput,
    hints: nextVariant.hints,
    correctAnswer: nextVariant.correctAnswer,
  }

  if (solutionPromptPart) {
    promptState.solutionPromptPart = solutionPromptPart
  }

  // generate new prompt for variant message
  const prompt = generatePrompt(promptState)

  // generate system message for gpt
  const promptForGptMessage: THistoryMessageOpenText = {
    role: MessageRole.System,
    content: prompt,
    currentVariantId: nextVariant.id,
  }

  // generate history obj and save to db
  updatedHistory = {
    ...updatedHistory,
    messages: [...updatedHistory.messages, variantMessage, promptForGptMessage],
    lastVariant,
    usedVariantsIds: updatedUsedVariantsIds,
    isLastVariantCompleted: false,
  }

  return {
    updatedHistory,
    messagesToSave: [variantMessage, promptForGptMessage],
  }
}

export function addNextVariantMessageToMultiChatModeOpenTextHistory(
  lessonContent: ILessonContentMultiModeChatOpenText,
  nextVariant: TLastVariantOpenText,
  history: IMultiModeChatHistoryItemOpenTextFulfilled,
  solutionPromptPart: string | undefined = undefined,
  themeMMRs?: {
    [themeId: string]: number
  },
  courseAdditionalInfo?: string,
): {
  updatedHistory: IMultiModeChatHistoryItemOpenTextFulfilled
  messagesToSave: TMultiModeChatHistoryMessageOpenText[]
} {
  let updatedHistory: IMultiModeChatHistoryItemOpenTextFulfilled = {
    ...history,
  }

  // generate variant message content
  const messageContent: TMessageContentObject = {
    question: nextVariant.question,
    correctAnswer: nextVariant.correctAnswer,
    variantId: nextVariant.id,
    isNextVariant: true,
  }

  // generate variant message
  const variantMessage: TMultiModeChatHistoryMessageOpenText = {
    role: MessageRole.Variant,
    content: messageContent,
    isQuestion: false,
    currentVariantId: nextVariant.id,
    attemptCount: 0,
    givenHintCount: 0,
    currentChatMode: updatedHistory.currentChatMode,
  }

  // update used variants ids
  const updatedUsedVariantsIds: string[] = [
    ...(updatedHistory.usedVariantsIds || []),
    nextVariant.id,
  ]

  // generate last variant
  const lastVariant: TLastVariantOpenText = {
    ...nextVariant,
    hintsCount: nextVariant.hints?.length || 0,
    isAnswered: false,
    attemptCount: 0,
    givenHintCount: 0,
    answerRevealed: false,
    isAnsweredCorrectly: false,
    isLastQuestionCompleted: false,
  }

  const themeMmr = lessonContent.themeId
    ? themeMMRs?.[lessonContent.themeId]
    : undefined
  const studentMmr = themeMmr || DEFAULT_MMR

  // find next not answered variant
  const hasNextVariant = !!findNextVariant(
    studentMmr,
    lessonContent.variants,
    updatedUsedVariantsIds,
  )

  // update hasNextVariant if there not answered harder variant found
  if (hasNextVariant) {
    lastVariant.hasNextVariant = true
  }

  // generate prompt system message for next variant
  const promptState: IMultiModePromptState = {
    question: nextVariant.question,
    questionTextInput: nextVariant.questionTextInput,
    correctAnswer: nextVariant.correctAnswer,
  }

  if (solutionPromptPart) {
    promptState.solutionPromptPart = solutionPromptPart
  }

  if (courseAdditionalInfo) {
    promptState.courseAdditionalInfo = courseAdditionalInfo
  }

  // generate new prompt for variant message
  const prompt = generateMultiModePrompt(promptState)

  // generate system message for gpt
  const promptForGptMessage: TMultiModeChatHistoryMessageOpenText = {
    role: MessageRole.System,
    content: prompt,
    currentVariantId: nextVariant.id,
    currentChatMode: updatedHistory.currentChatMode,
  }

  // generate history obj and save to db
  updatedHistory = {
    ...updatedHistory,
    messages: [...updatedHistory.messages, variantMessage, promptForGptMessage],
    lastVariant,
    usedVariantsIds: updatedUsedVariantsIds,
    isLastVariantCompleted: false,
  }

  return {
    updatedHistory,
    messagesToSave: [variantMessage, promptForGptMessage],
  }
}

// Visely Score (MMR) hint penalties stop after this many hints; further hints are free.
export const HINT_PENALTY_LIMIT = 3

export function getHintMmrLoss(
  variantHintsCount: number,
  currentVariantMmr: number,
  bookedCourseVariantMmr: number,
  isHintRequest: boolean = true,
): number {
  if (!isHintRequest) return 0

  const totalHintsCount = variantHintsCount || 3
  const opponentRating = currentVariantMmr

  const maxLoss = getMaxLossForGame(bookedCourseVariantMmr, opponentRating)

  const maxHintLoss = Math.min(maxLoss, K_FACTOR / 2)
  const eloDeductionPerHint = Math.round(maxHintLoss / totalHintsCount)

  return eloDeductionPerHint
}

export interface IHintToSpeakableTextPromptArgs {
  hint: string
  question: string
  questionTextInput?: string
}

export function hintToSpeakableTextPrompt({
  hint,
  question,
  questionTextInput,
}: IHintToSpeakableTextPromptArgs): string {
  const questionForDisplay = removeBase64Images(question)
  const questionForGpt = questionTextInput || questionForDisplay

  const promptParts = [
    `Your task is to convert provided hint to a spoken-like text suitable for speech synthesis.`,
    `Replace all KaTeX syntax and symbols with their corresponding plain text descriptions. For example, instead of writing "$u = 10\\\\frac{m}{s}$", express it as "u equals ten meters per second". Similarly, replace mathematical symbols like "$\\times$" with "times" or "$\\div$" with "divided by".`,
    `The goal is to make the text easily understandable when spoken aloud by a virtual avatar.`,
    `For better understanding context use "Question" field. Hints are used to help student find an answer for this question.`,
    `Keep in mind that both question and hint may be in KaTeX, LaTex syntax or regular text string.`,
    `Question: ${questionForGpt}`,
    `Hint: ${hint}`,
  ]

  const prompt = promptParts.join(' ')

  return prompt
}

export function extractPlainTextFromLexicalObj(
  stringifiedJson: string,
): string | undefined {
  let hasError = false
  // Create an editor instance
  const editor = createEditor({
    onError: (error) => {
      console.error('extractPlainTextFromLexicalObj error :>> ', error)

      hasError = true
    },
  })

  // Parse the JSON string into a Lexical EditorState
  const editorState = editor.parseEditorState(stringifiedJson)

  let plainText: string | undefined = undefined

  editorState.read(() => {
    const root = $getRoot()
    plainText = root.getTextContent()
  })

  if (hasError) {
    return undefined
  }

  return plainText
}

type LaTexRootObjectNode = {
  type: string
  text?: string
  equation?: string
  children?: LaTexRootObjectNode[]
}

type LaTexRootObject = {
  root: LaTexRootObjectNode
}

export function extractHintFromStringifiedObject(
  str: string,
): string | undefined {
  // Parse the stringified object into a TypeScript object
  const obj: LaTexRootObject | undefined = safeJsonParse<LaTexRootObject>(str)

  // Helper function to traverse the structure and extract the text and equation
  function traverse(node: LaTexRootObjectNode): string {
    let result = ''

    if (node.children && Array.isArray(node.children)) {
      for (let child of node.children) {
        result += traverse(child)
      }
    }

    if (node.type === 'text' && node.text) {
      result += node.text
    } else if (node.type === 'equation' && node.equation) {
      result += `$$${node.equation}$$`
    }

    return result
  }

  // Start the traversal from the root node or set as undefined if safeJsonParse fails
  const result = !!obj ? traverse(obj.root) : undefined

  return result
}

export function questionToSpeakableTextPrompt(question: string): string {
  const questionForDisplay = removeBase64Images(question)

  const promptParts = [
    `Your task is to convert the provided question and hint text to a spoken-like text suitable for speech synthesis.`,
    `Follow these steps to handle KaTeX syntax in the hint text:`,
    `1. Search for any mathematical equations or expressions using KaTeX syntax in the hint text.`,
    `2. Ensure that all KaTeX expressions are properly wrapped in dollar signs ("$ ... $") and replace all single backslashes ("\\") with double backslashes ("\\\\").`,
    `3. For example:`,
    `   - If the hint contains "u = 10\\frac{m}{s}", update it to "$u = 10\\\\frac{m}{s}$".`,
    `   - If the hint contains "\\times", update it to "$\\\\times$".`,
    `4. After identifying and fixing KaTeX expressions, convert the hint text into spoken-like text by replacing mathematical symbols with their plain-text equivalents. For example:`,
    `   - Replace "$u = 10\\\\frac{m}{s}$" with "u equals ten meters per second".`,
    `   - Replace "$\\\\times$" with "times".`,
    `The goal is to make the text easily understandable when spoken aloud by a virtual avatar.`,
    `5. Double-check the original hint text to ensure that all KaTeX expressions are correctly rendered and properly updated.`,
    `6. Use the provided 'kaTexOperatorsArray' to identify expressions and mathematical equations with KaTeX syntax in the hint text.`,
    `Question: ${questionForDisplay}`,
    `kaTexOperatorsArray: ${JSON.stringify(kaTexOperators)}`,
  ]

  const prompt = promptParts.join(' ')

  return prompt
}

export function scrollToUserLastMessage(
  containerElement: HTMLElement | null,
  messageElement: HTMLElement | null,
) {
  if (!containerElement) {
    return
  }

  // Small delay to ensure all elements are rendered
  setTimeout(() => {
    try {
      // First, scroll to the bottom to ensure we can see the latest messages
      containerElement.scrollTo({
        top: containerElement.scrollHeight,
        behavior: 'smooth',
      })

      if (!messageElement) {
        return
      }

      // Small delay before the second scroll to ensure the first one completes
      setTimeout(() => {
        try {
          // Calculate the exact position to scroll to
          // Using getBoundingClientRect() is more robust than offsetTop as it's independent of offsetParent
          const containerRect = containerElement.getBoundingClientRect()
          const elementRect = messageElement.getBoundingClientRect()
          const scrollPosition =
            elementRect.top - containerRect.top + containerElement.scrollTop

          // Smooth scroll to the final position
          containerElement.scrollTo({
            top: scrollPosition,
            behavior: 'smooth',
          })
        } catch (error) {
          console.error('Error during second scroll:', error)
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
        }
      }, 100) // Slightly longer delay for the second scroll
    } catch (error) {
      console.error('Error during first scroll:', error)

      if (!messageElement) {
        return
      }
      // Fallback to simple scrollIntoView if the above fails
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
    }
  }, 30) // Initial delay for the first scroll
}

export function scrollChatContainerBottom(
  containerElement: HTMLElement | null,
) {
  if (!containerElement) {
    return
  }

  // Scroll to the bottom of the container
  containerElement.scrollTo({
    top: containerElement.scrollHeight,
    behavior: 'smooth',
  })
}

export function calculateLastMessageMinHeight({
  maxContainerHeightPx,
  lastUserMessageRef,
  additionalHeightToReduce = 0,
}: {
  maxContainerHeightPx: number
  lastUserMessageRef: HTMLElement | null
  additionalHeightToReduce?: number
}): number {
  if (!lastUserMessageRef) {
    return 0
  }

  // Ensure the max container height is a valid number.
  if (typeof maxContainerHeightPx !== 'number' || maxContainerHeightPx <= 0) {
    return 0
  }

  try {
    // 1. Get the actual height of the last user message using getBoundingClientRect().
    // This method provides the most accurate dimensions of the rendered element.
    const userMessageHeight: number =
      lastUserMessageRef.getBoundingClientRect().height

    // 2. Calculate the required min-height:
    // MinHeight = Max Container Height - Actual User Message Height
    const minHeight: number =
      maxContainerHeightPx - userMessageHeight - additionalHeightToReduce

    // 3. Ensure the height is not negative.
    const safeMinHeight: number = Math.max(0, minHeight)

    // 4. Return the result in the standard CSS string format
    return safeMinHeight
  } catch (e) {
    // Catch potential errors during DOM manipulation (e.g., element not in document yet)
    return 0
  }
}
